import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, PlayCircle, FileText, Plus } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { SUBJECTS } from "./Aprende";
import { useAuth } from "@/context/AuthContext";

export default function Subject() {
  const { subject } = useParams();
  const meta = SUBJECTS[subject];
  const { isAdmin } = useAuth();
  const [lessons, setLessons] = useState(null);

  useEffect(() => {
    api.get(`/lessons`, { params: { subject } }).then((r) => setLessons(r.data)).catch(() => setLessons([]));
  }, [subject]);

  if (!meta) return <div className="max-w-7xl mx-auto px-5 py-20">Disciplina no encontrada.</div>;

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-24 pb-12">
      <Link to="/aprende" className="inline-flex items-center gap-2 text-ink-500 hover:text-nasa-blue text-sm font-semibold mb-8" data-testid="back-aprende">
        <ArrowLeft size={16} /> Volver a Aprende
      </Link>

      <div className="flex flex-wrap items-end justify-between gap-6 pb-6 border-b-2 border-paper-200">
        <div>
          <div className="flex items-center gap-3 mb-3"><span className="accent-rule" /><span className="eyebrow text-nasa-blue">APRENDE · {meta.name.toUpperCase()}</span></div>
          <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl flex items-center gap-4 text-nasa-blue">
            <meta.icon size={42} />
            {meta.name}
          </h1>
        </div>
        {isAdmin && (
          <Link to={`/admin/leccion/nueva?subject=${subject}`} data-testid="add-lesson-btn" className="inline-flex items-center gap-2 bg-nasa-blue text-white font-semibold px-5 py-2.5 rounded-full hover:bg-nasa-navy transition-colors">
            <Plus size={16} /> Nuevo apartado
          </Link>
        )}
      </div>

      {lessons === null ? (
        <p className="text-ink-500 mt-16">Cargando…</p>
      ) : lessons.length === 0 ? (
        <div className="mt-16 border border-dashed border-paper-300 bg-paper-50 p-14 text-center">
          <FileText className="mx-auto text-ink-400" size={40} />
          <p className="text-ink-700 mt-4 font-medium">Todavía no hay apartados en {meta.name}.</p>
          {isAdmin && <p className="text-ink-500 text-sm mt-1">Usa «Nuevo apartado» para crear el primero.</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7 mt-10">
          {lessons.map((l, i) => (
            <motion.div key={l.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
              <Link
                to={`/aprende/${subject}/${l.id}`}
                data-testid={`lesson-card-${l.id}`}
                className="group block h-full bg-white border border-paper-200 hover:border-nasa-blue transition-colors overflow-hidden"
              >
                <div className="relative h-40 bg-paper-100 overflow-hidden">
                  {l.cover_image ? (
                    <img src={mediaUrl(l.cover_image)} alt={l.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full science-grid flex items-center justify-center">
                      <meta.icon className="text-nasa-blue/20" size={52} />
                    </div>
                  )}
                  {l.video_url && (
                    <span className="absolute top-3 right-3 flex items-center gap-1 text-xs font-semibold bg-white px-2 py-1 rounded-full text-nasa-blue shadow"><PlayCircle size={13} /> vídeo</span>
                  )}
                  {!l.published && <span className="absolute top-3 left-3 eyebrow text-[9px] bg-nasa-blue text-white px-2 py-1">BORRADOR</span>}
                </div>
                <div className="p-6">
                  <h3 className="font-display font-bold text-xl text-nasa-blue group-hover:text-nasa-bright transition-colors">{l.title}</h3>
                  {l.description && <p className="text-ink-700 text-sm mt-2 line-clamp-2">{l.description}</p>}
                  <div className="flex items-center gap-3 mt-4 eyebrow text-ink-400 text-[10px]">
                    {l.practice?.length > 0 && <span>{l.practice.length} PRÁCTICAS</span>}
                    {l.slides?.length > 0 && <span>{l.slides.length} SLIDES</span>}
                  </div>
                  <span className="inline-flex items-center gap-1.5 mt-4 text-sm font-bold text-nasa-bright group-hover:gap-2.5 transition-all">Abrir apartado <ArrowRight size={14} /></span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
